import { useCallback, useLayoutEffect, useRef, useState } from 'react'

import { useMobileDetect } from '@/features/mobile-detect-context/mobile-detect-provider'

interface UseResponsiveHeightOptions {
    debounceMs?: number
}

interface UseResponsiveHeightReturn {
    containerRef: React.RefObject<HTMLDivElement>
    rowHeight: number
}

export const useResponsiveHeight = ({
    debounceMs = 16,
}: UseResponsiveHeightOptions = {}): UseResponsiveHeightReturn => {
    const containerRef = useRef<HTMLDivElement>(null)
    const { isMobile } = useMobileDetect()
    const [rowHeight, setRowHeight] = useState(0)

    const calculateRowHeight = useCallback(() => {
        const gridPadding = isMobile ? 4 : 10
        const containerWidth = containerRef?.current?.offsetWidth || 0
        const itemsWidth = (containerWidth - gridPadding * 2) / 3
        const newRowHeight = itemsWidth * 2 + gridPadding * 2

        setRowHeight(newRowHeight)
    }, [isMobile])

    useLayoutEffect(() => {
        calculateRowHeight()

        const container = containerRef.current
        if (!container) return

        let resizeTimeoutId: NodeJS.Timeout | null = null

        const resizeObserver = new ResizeObserver(() => {
            if (resizeTimeoutId) {
                clearTimeout(resizeTimeoutId)
            }

            resizeTimeoutId = setTimeout(() => {
                calculateRowHeight()
            }, debounceMs)
        })

        resizeObserver.observe(container)

        return () => {
            if (resizeTimeoutId) {
                clearTimeout(resizeTimeoutId)
            }
            resizeObserver.disconnect()
        }
    }, [calculateRowHeight, debounceMs])

    return {
        containerRef,
        rowHeight,
    }
}
